import React, { useEffect, useRef, useState } from 'react';
import {
  X,
  ChevronDown,
  Check,
  Search,
  CalendarDays,
  Layers,
  AlertTriangle,
  Flame,
  Minus,
  TrendingUp,
} from 'lucide-react';
import type { OrgMember } from '../../../hooks/useOrgMembers';
import type { EnterpriseProject } from '../../../types/enterpriseProject';
import { ActionService, type ActionPriority } from '../../../services/actionService';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  planProjectId?: string | null;
  urlPlanId?: string | null;
  planName?: string;
  orgMembers: OrgMember[];
  enterpriseProjects: EnterpriseProject[];
  onCreated: () => void;
  showToast?: (type: 'success' | 'error' | 'info', message: string) => void;
  defaultAssignedTo?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function initials(name: string): string {
  return name
    .split(' ')
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

const PRIORITY_CONFIG: {
  value: ActionPriority;
  label: string;
  icon: React.ReactNode;
  active: string;
  inactive: string;
}[] = [
  {
    value: 'low',
    label: 'Baixa',
    icon: <Minus size={10} />,
    active: 'bg-slate-500/15 text-slate-600 border-slate-400 dark:text-slate-300',
    inactive:
      'border-[var(--border-default)] text-[var(--text-muted)] hover:bg-[var(--button-hover)]',
  },
  {
    value: 'medium',
    label: 'Média',
    icon: <TrendingUp size={10} />,
    active: 'bg-blue-500/15 text-blue-600 border-blue-400 dark:text-blue-300',
    inactive:
      'border-[var(--border-default)] text-[var(--text-muted)] hover:bg-[var(--button-hover)]',
  },
  {
    value: 'high',
    label: 'Alta',
    icon: <AlertTriangle size={10} />,
    active: 'bg-orange-500/15 text-orange-600 border-orange-400 dark:text-orange-300',
    inactive:
      'border-[var(--border-default)] text-[var(--text-muted)] hover:bg-[var(--button-hover)]',
  },
  {
    value: 'urgent',
    label: 'Urgente',
    icon: <Flame size={10} />,
    active: 'bg-red-500/15 text-red-600 border-red-400 dark:text-red-300',
    inactive:
      'border-[var(--border-default)] text-[var(--text-muted)] hover:bg-[var(--button-hover)]',
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

export function NewActionModal({
  open,
  onClose,
  organizationId,
  planProjectId,
  urlPlanId,
  planName,
  orgMembers,
  enterpriseProjects,
  onCreated,
  showToast,
  defaultAssignedTo,
}: Props): React.ReactElement | null {
  // ── Form state ─────────────────────────────────────────────
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState(defaultAssignedTo ?? '');
  const [deadline, setDeadline] = useState('');
  const [priority, setPriority] = useState<ActionPriority>('medium');
  const [linkedProjectId, setLinkedProjectId] = useState<string>(planProjectId ?? '');

  // ── Dropdown state ─────────────────────────────────────────
  const [responsibleOpen, setResponsibleOpen] = useState(false);
  const [responsibleSearch, setResponsibleSearch] = useState('');
  const [projectOpen, setProjectOpen] = useState(false);

  // ── Submission ─────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);

  // Reset state (including defaultAssignedTo) whenever modal opens
  useEffect(() => {
    if (open) {
      setTitle('');
      setDescription('');
      setAssignedTo(defaultAssignedTo ?? '');
      setDeadline('');
      setPriority('medium');
      setLinkedProjectId(planProjectId ?? '');
      setResponsibleOpen(false);
      setResponsibleSearch('');
      setProjectOpen(false);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  // ── Derived ────────────────────────────────────────────────
  const assignedMember = orgMembers.find(m => m.id === assignedTo) ?? null;
  const filteredResponsible = orgMembers.filter(
    m =>
      !responsibleSearch ||
      m.name.toLowerCase().includes(responsibleSearch.toLowerCase()) ||
      m.email.toLowerCase().includes(responsibleSearch.toLowerCase()),
  );
  const linkedProject =
    enterpriseProjects.find(p => p.id === linkedProjectId) ?? null;

  // ── Reset ──────────────────────────────────────────────────
  function reset() {
    setTitle('');
    setDescription('');
    setAssignedTo('');
    setDeadline('');
    setPriority('medium');
    setLinkedProjectId(planProjectId ?? '');
    setResponsibleOpen(false);
    setResponsibleSearch('');
    setProjectOpen(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  // ── Submit ─────────────────────────────────────────────────
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle || !organizationId) return;

    setLoading(true);
    try {
      const result = await ActionService.create({
        organization_id: organizationId,
        project_id: linkedProjectId || planProjectId || undefined,
        plan_id: urlPlanId || undefined,
        title: trimmedTitle,
        description: description.trim() || undefined,
        status: 'pending',
        priority,
        due_date: deadline || undefined,
        assigned_to: assignedTo || undefined,
      });

      if (result) {
        showToast?.('success', 'Ação criada com sucesso.');
        onCreated();
        reset();
        onClose();
      } else {
        showToast?.('error', 'Falha ao criar ação. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  }

  // ── Input class ────────────────────────────────────────────
  const fieldClass =
    'w-full rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-app)] px-3 py-2 text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] transition-colors focus:border-blue-500 focus:outline-none';
  const labelClass =
    'block mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-[var(--text-muted)]';

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative z-10 flex w-full max-w-2xl flex-col rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-[0_24px_48px_rgba(0,0,0,0.28)]">

        {/* ── Header ─────────────────────────────────────────── */}
        <div className="flex shrink-0 items-center justify-between border-b border-[var(--border-default)] px-5 py-3.5">
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-[13px] font-semibold text-[var(--text-primary)]">
              Nova Ação
            </span>
            {planName && (
              <span className="truncate text-[11px] text-[var(--text-muted)]">
                {planName}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="ml-4 shrink-0 rounded-sm p-1 text-[var(--text-muted)] transition-colors hover:bg-[var(--button-hover)] hover:text-[var(--text-primary)]"
          >
            <X size={15} />
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────── */}
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="grid grid-cols-[3fr_2fr] divide-x divide-[var(--border-default)] overflow-y-auto">

            {/* ── Left column: Escopo ────────────────────────── */}
            <div className="flex flex-col gap-5 p-5">

              {/* Título */}
              <div>
                <label className={labelClass}>Título *</label>
                <input
                  ref={titleRef}
                  autoFocus
                  required
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="O que precisa ser feito?"
                  className={`${fieldClass} text-[14px] font-medium`}
                />
              </div>

              {/* Descrição */}
              <div className="flex flex-1 flex-col">
                <label className={labelClass}>Descrição / Contexto</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={5}
                  placeholder="Descreva o escopo técnico da ação, padrão esperado e barreiras..."
                  className={`${fieldClass} flex-1 resize-none leading-relaxed`}
                />
              </div>
            </div>

            {/* ── Right column: Metadados ────────────────────── */}
            <div className="flex flex-col gap-5 p-5">

              {/* Responsável */}
              <div>
                <label className={labelClass}>Responsável</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setResponsibleOpen(v => !v)}
                    className={`${fieldClass} flex items-center gap-2 text-left`}
                  >
                    {assignedMember ? (
                      <>
                        <span className="flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-sm bg-blue-600 text-[9px] font-bold text-white">
                          {initials(assignedMember.name)}
                        </span>
                        <span className="flex-1 truncate">{assignedMember.name}</span>
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); setAssignedTo(''); }}
                          className="shrink-0 rounded p-0.5 text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                        >
                          <X size={10} />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-[var(--text-muted)]">Selecionar responsável...</span>
                        <ChevronDown size={12} className="shrink-0 text-[var(--text-muted)]" />
                      </>
                    )}
                  </button>

                  {responsibleOpen && (
                    <div className="absolute left-0 right-0 top-full z-50 mt-0.5 overflow-hidden rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-lg">
                      <div className="flex items-center gap-2 border-b border-[var(--border-default)] px-2.5 py-1.5">
                        <Search size={11} className="shrink-0 text-[var(--text-muted)]" />
                        <input
                          type="text"
                          autoFocus
                          placeholder="Buscar membro..."
                          value={responsibleSearch}
                          onChange={e => setResponsibleSearch(e.target.value)}
                          className="w-full bg-transparent text-[12px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none"
                        />
                      </div>
                      <div className="max-h-[160px] overflow-y-auto">
                        {filteredResponsible.length === 0 ? (
                          <p className="px-3 py-2.5 text-[11px] text-[var(--text-muted)]">
                            Nenhum resultado
                          </p>
                        ) : (
                          filteredResponsible.map(member => {
                            const isSelected = assignedTo === member.id;
                            return (
                              <button
                                key={member.id}
                                type="button"
                                onClick={() => {
                                  setAssignedTo(member.id);
                                  setResponsibleOpen(false);
                                  setResponsibleSearch('');
                                }}
                                className={`flex w-full items-center gap-2.5 px-3 py-1.5 text-left transition-colors hover:bg-[var(--button-hover)] ${isSelected ? 'bg-blue-500/10' : ''}`}
                              >
                                <div
                                  className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-sm text-[9px] font-bold ${
                                    isSelected
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]'
                                  }`}
                                >
                                  {isSelected ? <Check size={11} /> : initials(member.name)}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-[12px] text-[var(--text-primary)]">
                                    {member.name}
                                  </p>
                                  <p className="truncate text-[10px] text-[var(--text-muted)]">
                                    {member.email}
                                  </p>
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Prazo */}
              <div>
                <label className={labelClass}>
                  <CalendarDays size={10} className="inline-block mr-1 -mt-px" />
                  Prazo
                </label>
                <input
                  type="date"
                  value={deadline}
                  onChange={e => setDeadline(e.target.value)}
                  className={fieldClass}
                />
              </div>

              {/* Prioridade */}
              <div>
                <label className={labelClass}>Prioridade</label>
                <div className="grid grid-cols-2 gap-1.5">
                  {PRIORITY_CONFIG.map(p => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => setPriority(p.value)}
                      className={`flex items-center justify-center gap-1.5 rounded-[3px] border px-2 py-1.5 text-[11px] font-semibold transition-all ${
                        priority === p.value ? p.active : p.inactive
                      }`}
                    >
                      {p.icon}
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Projeto / VSM Vinculado */}
              <div>
                <label className={labelClass}>
                  <Layers size={10} className="inline-block mr-1 -mt-px" />
                  Projeto / VSM Vinculado
                  <span className="ml-1 normal-case text-[9px] font-normal">(opcional)</span>
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setProjectOpen(v => !v)}
                    className={`${fieldClass} flex items-center gap-2 text-left`}
                  >
                    {linkedProject ? (
                      <>
                        <span className="flex-1 truncate">{linkedProject.name}</span>
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); setLinkedProjectId(''); }}
                          className="shrink-0 rounded p-0.5 text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                        >
                          <X size={10} />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-[var(--text-muted)]">Nenhum vínculo</span>
                        <ChevronDown size={12} className="shrink-0 text-[var(--text-muted)]" />
                      </>
                    )}
                  </button>

                  {projectOpen && (
                    <div className="absolute left-0 right-0 top-full z-50 mt-0.5 overflow-hidden rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-lg">
                      <div className="max-h-[160px] overflow-y-auto">
                        <button
                          type="button"
                          onClick={() => { setLinkedProjectId(''); setProjectOpen(false); }}
                          className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12px] text-[var(--text-muted)] transition-colors hover:bg-[var(--button-hover)] ${!linkedProjectId ? 'bg-[var(--button-hover)]' : ''}`}
                        >
                          <span className="flex-1">Nenhum vínculo</span>
                          {!linkedProjectId && <Check size={11} />}
                        </button>
                        <div className="border-t border-[var(--border-default)]" />
                        {enterpriseProjects.length === 0 ? (
                          <p className="px-3 py-2.5 text-[11px] text-[var(--text-muted)]">
                            Nenhum projeto cadastrado
                          </p>
                        ) : (
                          enterpriseProjects.map(project => (
                            <button
                              key={project.id}
                              type="button"
                              onClick={() => { setLinkedProjectId(project.id); setProjectOpen(false); }}
                              className={`flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors hover:bg-[var(--button-hover)] ${linkedProjectId === project.id ? 'bg-blue-500/10' : ''}`}
                            >
                              <span className="flex-1 truncate text-[12px] text-[var(--text-primary)]">
                                {project.name}
                              </span>
                              {linkedProjectId === project.id && (
                                <Check size={11} className="shrink-0 text-blue-500" />
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Footer ─────────────────────────────────────────── */}
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-[var(--border-default)] px-5 py-3.5">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="rounded-[3px] px-4 py-2 text-[13px] font-medium text-[var(--text-secondary)] transition-colors hover:bg-[var(--button-hover)] disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !title.trim()}
              className="flex min-w-[110px] items-center justify-center gap-2 rounded-[3px] bg-blue-600 px-5 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <>
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  Criando...
                </>
              ) : (
                'Criar Ação'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
